import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { RosterRow, TeamCard } from '@/api/base-types';
import type { UnlinkedPerson } from '@/api/m3-types';
import { useBaseMutation, useModules, useTeam, useTeamMembers } from '@/api/base';
import { api } from '@/api/client';
import { ActionMenu, RowActions, type MenuItem } from '@/components/kit/ActionMenu';
import { Button } from '@/components/kit/Button';
import { TextLink } from '@/components/kit/Controls';
import { Breadcrumbs, Kv } from '@/components/kit/Layout';
import { MetricRow } from '@/components/kit/MetricRow';
import { Alert, EmptyState, ErrorState, Skeleton, TableSkeleton } from '@/components/kit/Note';
import { ObjectTabs, SectionHeader } from '@/components/kit/ObjectTabs';
import { Overview, Panel, Signals } from '@/components/kit/Panel';
import { PersonHeader, Who } from '@/components/kit/Person';
import { Pill } from '@/components/kit/Pill';
import { useToast } from '@/components/kit/Toast';
import { formatPhone, years } from '@/lib/phone';
import { ArchiveModal } from '../shared/ArchiveModal';
import { FeedPanel } from '../shared/FeedPanel';
import { JournalModal } from '../shared/JournalModal';
import { ruDate } from '../shared/labels';
import { MergeModal } from '../shared/MergeModal';
import { useLinkActions } from '../shared/useLinkActions';
import { errorText } from '../shared/useUndoable';
import { AddMemberModal, ChangeHeadModal, EditTeamModal } from './TeamDialogs';
import { NickLink } from '../shared/NickLink';
import { CurrentParticipation, TeamHistory, UnlinkedPeople } from '../shared/Participation';

type Tab = 'overview' | 'members' | 'history';
type Dialog = 'edit' | 'head' | 'member' | 'merge' | 'journal' | 'archive';

/**
 * Карточка команды (БЛ 11.3, viewTeam в макете). «История участия»,
 * «Финансы», «Текущее участие», кнопка «Зарегистрировать» и цифры
 * выступлений и денег появятся с модулями «Регистрации» и «Оплаты»
 * (ТЗ M2 7.3). Без них — «Обзор» и «Участники».
 */
export function TeamPage() {
  const { teamId, tab } = useParams();
  const navigate = useNavigate();
  const team = useTeam(teamId);
  const modules = useModules();
  const [dialog, setDialog] = useState<Dialog | null>(null);
  // «Завести клиентом» из «Выступали без карточки» — данные из строки номера
  const [prefill, setPrefill] = useState<MemberPrefill | undefined>(undefined);
  const close = () => setDialog(null);
  // «История участия», «Текущее участие» и «Выступали без карточки» — с модулем «Регистрации» (ТЗ M3 7.2)
  const withRegs = Boolean(modules.data?.registrations);
  const current: Tab = tab === 'members' ? 'members' : tab === 'history' && withRegs ? 'history' : 'overview';
  const crumbs = (label: string) => (
    <Breadcrumbs items={[{ label: 'Команды', onClick: () => navigate('/teams') }, { label }]} />
  );

  if (team.isError) {
    return (
      <div className="main">
        {crumbs('Команда')}
        <ErrorState message={team.error.message} onRetry={() => void team.refetch()} />
      </div>
    );
  }
  if (team.isLoading || !team.data) {
    return (
      <div className="main">
        {crumbs('…')}
        <Skeleton width={320} height={28} />
      </div>
    );
  }

  const t = team.data;
  return (
    <div className="main has-abar">
      {crumbs(t.name)}
      <TeamHeader
        team={t}
        onDialog={setDialog}
        primaryEdit={!withRegs}
        onRegister={withRegs ? () => navigate('/registrations/new') : undefined}
      />

      <Signals>
        {t.signals.studioArchived ? (
          <Alert>
            <b>Студия «{t.studio?.name}» в архиве.</b>
            <span>Команда ссылается на студию, которая больше не работает с нами.</span>
          </Alert>
        ) : null}
        {t.signals.awaitingHead ? <AwaitingHead team={t} /> : null}
      </Signals>

      <ObjectTabs
        value={current}
        onChange={(next) => navigate(`/teams/${t.id}${next === 'overview' ? '' : `/${next}`}`, { replace: true })}
        items={[
          { value: 'overview', label: 'Обзор' },
          { value: 'members', label: 'Участники' },
          ...(withRegs ? [{ value: 'history' as const, label: 'История участия' }] : []),
        ]}
      />

      {current === 'overview' ? (
        <>
          <MetricRow variant="sum" items={[{ label: 'Участников', value: t.membersCount, dim: !t.membersCount }]} />
          {withRegs ? (
            <div className="m3gap">
              <CurrentParticipation teamId={t.id} onRegister={() => navigate('/registrations/new')} />
            </div>
          ) : null}
          {/* Главная кнопка шапки — «Зарегистрировать»; «Изменить» уходит в панель данных, как в макете M2 */}
          <Overview left={<DataPanel team={t} onEdit={withRegs ? () => setDialog('edit') : undefined} />} right={<FeedPanel entity="team" id={t.id} placeholder="Что важно знать о команде" />} />
        </>
      ) : current === 'history' ? (
        <TeamHistory teamId={t.id} />
      ) : (
        <RosterPanel
          team={t}
          onAdd={() => {
            setPrefill(undefined);
            setDialog('member');
          }}
          onPromote={(p) => {
            setPrefill({ lastName: p.lastName ?? '', firstName: p.firstName ?? '', birthDate: p.birthDate, phone: p.phone ?? '' });
            setDialog('member');
          }}
          withUnlinked={withRegs}
        />
      )}

      {dialog === 'edit' ? <EditTeamModal team={t} onClose={close} /> : null}
      {dialog === 'head' ? <ChangeHeadModal team={t} onClose={close} /> : null}
      {dialog === 'member' ? <MemberDialog team={t} initial={prefill} onClose={close} /> : null}
      {dialog === 'journal' ? <JournalModal entity="team" id={t.id} onClose={close} /> : null}
      {dialog === 'archive' ? <ArchiveModal entity="team" id={t.id} name={t.name} onClose={close} /> : null}
      {dialog === 'merge' ? <MergeModal entity="team" id={t.id} onClose={close} /> : null}
    </div>
  );
}

function useConfirm(team: TeamCard) {
  const toast = useToast();
  const confirm = useBaseMutation(() => api.post(`/teams/${team.id}/confirm`));
  return () =>
    confirm.mutate(undefined, {
      onSuccess: () => toast.show('Данные команды подтверждены'),
      onError: (e) => toast.showError(errorText(e)),
    });
}

function AwaitingHead({ team }: { team: TeamCard }) {
  const confirm = useConfirm(team);
  return (
    <Alert
      action={
        <Button small onClick={confirm}>
          Подтвердить данные
        </Button>
      }
    >
      <b>Данные ждут руководителя.</b>
      <span>Команду завёл менеджер: название, город и состав с его слов и могут поменяться.</span>
    </Alert>
  );
}

function TeamHeader({
  team: t,
  onDialog,
  primaryEdit,
  onRegister,
}: {
  team: TeamCard;
  onDialog: (d: Dialog) => void;
  primaryEdit: boolean;
  /** «Зарегистрировать» — главная кнопка, когда есть модуль «Регистрации» */
  onRegister?: () => void;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm(t);
  const unarchive = useBaseMutation(() => api.post(`/teams/${t.id}/unarchive`));
  const items: MenuItem[] = [
    ...(!t.dataConfirmed ? [{ label: 'Подтвердить данные', onClick: confirm }] : []),
    { label: 'Сменить руководителя…', onClick: () => onDialog('head') },
    { label: 'Слить с дублем…', onClick: () => onDialog('merge') },
    { label: 'Журнал изменений', onClick: () => onDialog('journal') },
    t.archivedAt
      ? {
          label: 'Вернуть из архива',
          onClick: () =>
            unarchive.mutate(undefined, {
              onSuccess: () => toast.show('Команда снова в работе'),
              onError: (e) => toast.showError(errorText(e)),
            }),
        }
      : { label: 'В архив…', danger: true, onClick: () => onDialog('archive') },
  ];
  return (
    <PersonHeader
      initials={t.initials}
      title={t.name}
      pills={t.archivedAt ? <Pill tone="mut">В архиве</Pill> : null}
      meta={
        <>
          <span>{t.city.name}</span>
          {t.studio ? (
            <TextLink onClick={() => navigate(`/studios/${t.studio!.id}`)}>студия {t.studio.name}</TextLink>
          ) : (
            <span className="off">без студии</span>
          )}
          <span>
            руководитель <TextLink onClick={() => navigate(`/clients/${t.head.id}`)}>{t.head.shortName}</TextLink>
          </span>
        </>
      }
      actions={
        <>
          {primaryEdit ? (
            <Button tone="primary" onClick={() => onDialog('edit')}>
              Изменить
            </Button>
          ) : null}
          {onRegister && !t.archivedAt ? (
            <Button tone="primary" onClick={onRegister}>
              Зарегистрировать
            </Button>
          ) : null}
          <ActionMenu items={items} />
        </>
      }
    />
  );
}

function DataPanel({ team: t, onEdit }: { team: TeamCard; onEdit?: () => void }) {
  const navigate = useNavigate();
  return (
    <Panel
      title="Данные"
      actions={
        onEdit ? (
          <Button small onClick={onEdit}>
            Изменить
          </Button>
        ) : undefined
      }
    >
      <Kv k="Название">{t.name}</Kv>
      <Kv k="Город команды">{t.city.name}</Kv>
      <Kv k="Instagram">
        <NickLink nick={t.instagram} network="instagram" />
      </Kv>
      <Kv k="Студия">
        {t.studio ? (
          <>
            <TextLink onClick={() => navigate(`/studios/${t.studio!.id}`)}>{t.studio.name}</TextLink>
            {t.studio.archived ? <span className="wtx"> · в архиве</span> : null}
          </>
        ) : (
          <span className="dim">без студии</span>
        )}
      </Kv>
      <Kv k="Руководитель">
        <TextLink onClick={() => navigate(`/clients/${t.head.id}`)}>{t.head.fullName}</TextLink>
        {/* Пробел вне .num: номер неразрывный, и перенос возможен только перед ним */}
        {t.head.phone ? (
          <>
            {' '}
            <span className="muted num">· {formatPhone(t.head.phone)}</span>
          </>
        ) : null}
      </Kv>
      <Kv k="Данные">
        {t.dataConfirmed ? 'подтверждены' : <span className="wtx">ждут руководителя</span>}
      </Kv>
      <Kv k="Ответственный">
        {t.responsible ? (
          <>
            {t.responsible.fullName} <span className="muted">· по руководителю</span>
          </>
        ) : (
          <span className="wtx">не назначен у руководителя</span>
        )}
      </Kv>
      <Kv k="Участников">
        <span className="num">{t.membersCount}</span>
      </Kv>
      <Kv k="В базе с">
        <span className="num">{ruDate(t.createdAt)}</span>
      </Kv>
    </Panel>
  );
}

type MemberPrefill = { lastName: string; firstName: string; birthDate: string | null; phone: string };

function MemberDialog({ team, initial, onClose }: { team: TeamCard; initial?: MemberPrefill; onClose: () => void }) {
  const roster = useTeamMembers(team.id);
  return (
    <AddMemberModal
      team={team}
      initial={initial}
      activeIds={(roster.data?.active ?? []).map((r) => r.client.id)}
      leftIds={(roster.data?.left ?? []).map((r) => r.client.id)}
      onClose={onClose}
    />
  );
}

const DOCS = {
  by_head: <span className="dim">через руководителя</span>,
  by_representative: <span className="dim">принимал представитель</span>,
  accepted: 'приняты',
};

/**
 * «Участники · Состав» (БЛ 11.3): в составе и выбывшие, возраст,
 * представитель с телефоном, документы; «⋯» — выбыл, убрать, вернуть.
 * «Выступали без карточки» — с модулем «Регистрации».
 */
function RosterPanel({
  team,
  onAdd,
  onPromote,
  withUnlinked,
}: {
  team: TeamCard;
  onAdd: () => void;
  onPromote: (p: UnlinkedPerson) => void;
  withUnlinked: boolean;
}) {
  const [seg, setSeg] = useState<'pool' | 'man'>('pool');
  const navigate = useNavigate();
  const roster = useTeamMembers(team.id);
  const links = useLinkActions();

  const row = (r: RosterRow, out: boolean) => (
    <tr key={r.membershipId} className="clk" onClick={() => navigate(`/clients/${r.client.id}`)}>
      <td data-col="primary">
        <Who
          initials={r.client.initials}
          name={r.client.fullName}
          sub={!out && r.possibleDuplicate ? <div className="sub wtx">возможный дубль</div> : null}
        />
      </td>
      <td data-col="hide">
        <span className="num">{r.client.age !== null ? years(r.client.age) : <span className="na">—</span>}</span>
        {r.client.birthDate ? <div className="sub num">{ruDate(r.client.birthDate)}</div> : null}
      </td>
      <td data-col="hide">
        {out ? (
          <span className="na">—</span>
        ) : !r.client.isMinor ? (
          <span className="dim">не нужен</span>
        ) : r.representatives.length ? (
          r.representatives.map((p) => (
            <div key={p.fullName}>
              {p.fullName}
              <div className="sub num">{formatPhone(p.phone)}</div>
            </div>
          ))
        ) : (
          <span className="dim">не указан</span>
        )}
      </td>
      <td data-col="hide">{out ? <span className="na">—</span> : DOCS[r.documents]}</td>
      <td className="r num muted" data-col="secondary">
        {out ? `выбыл(а) ${ruDate(r.leftOn)}` : `с ${ruDate(r.joinedOn)}`}
      </td>
      <td className="ra" data-col="hide" onClick={(e) => e.stopPropagation()}>
        <RowActions
          items={links.membership({
            id: r.membershipId,
            teamId: team.id,
            clientId: r.client.id,
            leftOn: r.leftOn,
            name: r.client.fullName,
          })}
        />
      </td>
    </tr>
  );

  const active = roster.data?.active ?? [];
  const left = roster.data?.left ?? [];

  return (
    <Panel>
      <SectionHeader
        segments={
          withUnlinked
            ? {
                value: seg,
                onChange: setSeg,
                items: [
                  { value: 'pool', label: 'Состав' },
                  { value: 'man', label: 'Выступали без карточки' },
                ],
              }
            : undefined
        }
        actions={<Button onClick={onAdd}>Включить в состав</Button>}
      />
      {seg === 'man' ? (
        <UnlinkedPeople teamId={team.id} onPromote={onPromote} />
      ) : roster.isError ? (
        <ErrorState message={roster.error.message} onRetry={() => void roster.refetch()} />
      ) : roster.isLoading ? (
        <TableSkeleton rows={4} cols={5} />
      ) : !active.length && !left.length ? (
        <EmptyState>
          В составе пока никого. Состав не обязателен: он нужен, чтобы подставлять людей в номера одним выбором.
        </EmptyState>
      ) : (
        <table className="tbl">
          <thead>
            <tr>
              <th className="c-mem srt">
                Участник <span className="ar">▲</span>
              </th>
              <th className="c-age">Возраст</th>
              <th className="c-rep">Представитель</th>
              <th className="c-con">Документы</th>
              <th className="r">В составе</th>
              <th className="c-ra" />
            </tr>
          </thead>
          <tbody>
            {active.length ? (
              <tr className="trg">
                <td colSpan={6}>
                  В составе<span className="c">{active.length}</span>
                </td>
              </tr>
            ) : null}
            {active.map((r) => row(r, false))}
            {left.length ? (
              <tr className="trg">
                <td colSpan={6}>
                  Выбыли<span className="c">{left.length}</span>
                </td>
              </tr>
            ) : null}
            {left.map((r) => row(r, true))}
          </tbody>
        </table>
      )}
    </Panel>
  );
}
